import React from 'react';
import PolicyLayout from '../components/PolicyLayout';
import SEOHead from '../components/SEOHead';
import { getSEOConfig } from '../utils/seoConfig';

const SecurityPolicyPage: React.FC = () => {
  const seoConfig = getSEOConfig('/security');

  return (
    <>
      <SEOHead
        title={seoConfig.title}
        description={seoConfig.description}
        keywords={seoConfig.keywords}
        ogType={seoConfig.ogType}
      />
      <PolicyLayout title="Security Policy" lastUpdated="October 2025">
        <p>
          At GelWel, we prioritize the security of your data and are committed to protecting your information
          through industry-leading security measures.
        </p>

        <h2>1. Data Encryption</h2>
        <p>
          We use robust encryption to protect your data:
        </p>

        <h3>Data in Transit</h3>
        <p>
          All data transmitted between your device and our servers is encrypted using SSL/TLS protocols.
          This ensures that your information cannot be intercepted or read by unauthorized parties during
          transmission.
        </p>

        <h3>Data at Rest</h3>
        <p>
          Your data stored on our servers is encrypted using industry-standard encryption algorithms. This
          includes your profile information, messages, photos, and other personal data.
        </p>

        <h2>2. Password Security</h2>
        <p>
          We take password security seriously:
        </p>
        <ul>
          <li>All passwords are stored using secure hashing algorithms with salt.</li>
          <li>We never store passwords in plain text.</li>
          <li>We require strong passwords that meet minimum security standards.</li>
          <li>We offer two-factor authentication for enhanced account protection.</li>
        </ul>

        <h3>Password Best Practices</h3>
        <p>
          To keep your account secure, we recommend:
        </p>
        <ul>
          <li>Using a unique password that you don't use for other services</li>
          <li>Creating a password with at least 8 characters, including letters, numbers, and symbols</li>
          <li>Never sharing your password with anyone</li>
          <li>Changing your password regularly, especially if you suspect unauthorized access</li>
        </ul>

        <h2>3. Access Controls</h2>
        <p>
          We implement strict access controls to protect your data:
        </p>
        <ul>
          <li>Access to personal data is limited to authorized personnel only.</li>
          <li>Employees with data access undergo background checks and security training.</li>
          <li>All access to user data is logged and monitored for suspicious activity.</li>
          <li>We use role-based access controls to ensure employees only access data necessary for their job.</li>
        </ul>

        <h2>4. Vulnerability Monitoring</h2>
        <p>
          We regularly monitor our systems for potential security vulnerabilities:
        </p>
        <ul>
          <li>Automated security scanning for known vulnerabilities</li>
          <li>Regular security audits and penetration testing</li>
          <li>Monitoring for unusual activity patterns that may indicate security threats</li>
          <li>Immediate response protocols for identified vulnerabilities</li>
        </ul>

        <h2>5. Third-Party Security</h2>
        <p>
          We carefully vet all third-party service providers who have access to user data:
        </p>
        <ul>
          <li>All vendors must meet our strict security standards</li>
          <li>We require third parties to sign data protection agreements</li>
          <li>Regular audits of third-party security practices</li>
          <li>Immediate termination of access if security standards are not met</li>
        </ul>

        <h2>6. Security Incident Response</h2>
        <p>
          In the event of a security incident:
        </p>
        <ul>
          <li>Our security team will immediately investigate and contain the issue.</li>
          <li>Affected users will be notified within 72 hours of discovery.</li>
          <li>We will provide clear information about what happened and what steps we're taking.</li>
          <li>We will offer guidance on how users can protect themselves.</li>
        </ul>

        <h2>7. Reporting Security Concerns</h2>
        <p>
          If you suspect unauthorized access to your account or discover a security vulnerability, contact us
          immediately at <a href="mailto:paulfortuneltd@gmail.com">paulfortuneltd@gmail.com</a> with the
          subject line "Security Concern."
        </p>

        <h3>What to Do If Your Account Is Compromised</h3>
        <ol>
          <li>Change your password immediately</li>
          <li>Enable two-factor authentication if not already active</li>
          <li>Review recent account activity for suspicious behavior</li>
          <li>Contact our support team right away</li>
          <li>Check other accounts where you may have used the same password</li>
        </ol>

        <h2>8. Data Backup and Recovery</h2>
        <p>
          We maintain secure backups of your data to ensure business continuity and prevent data loss.
          All backups are encrypted and stored in secure, geographically distributed locations.
        </p>

        <h2>9. Security Updates</h2>
        <p>
          We continuously improve our security measures to protect against emerging threats. We recommend
          keeping your app updated to the latest version to ensure you have the most recent security features.
        </p>

        <h2>10. Contact</h2>
        <p>
          For security questions or to report a security concern:{' '}
          <a href="mailto:paulfortuneltd@gmail.com">paulfortuneltd@gmail.com</a>
        </p>
      </PolicyLayout>
    </>
  );
};

export default SecurityPolicyPage;
